import React from 'react';
import { useRouter, type RegisteredRouter, type ToOptions } from '@tanstack/react-router';

/**
 * Returns a function that turns a typed route descriptor (`{ to, params, search }`)
 * into an href string, for use as a `DataTable` `getRowHref`.
 *
 * Driving table rows via react-aria's link mechanism (rather than programmatic
 * `navigate()`) gives "open in new tab" on cmd/ctrl-click, while a plain
 * left-click still navigates client-side because the app wraps its tree in
 * react-aria's `RouterProvider` (see `router.tsx`), which routes plain link
 * activations through `router.navigate`. (Table rows are `<tr>`, not real `<a>`
 * anchors, so right-click → "Open in new tab" and middle-click aren't available.)
 *
 * The descriptor is the same shape `<Link>`/`useNavigate()` accept, so `to` and
 * `params` stay fully type-checked against the route tree (the callback is
 * generic over `to`, which is what lets `params` infer per route).
 *
 * @example
 * const buildHref = useRowLinkBuilder();
 * <DataTable
 *   getRowHref={(run) =>
 *     buildHref({ to: '/runs/test/$executionId', params: { executionId: run.id } })
 *   }
 * />
 */
export function useRowLinkBuilder() {
  const router = useRouter();
  return React.useCallback(
    <TTo extends string | undefined = undefined, TFrom extends string = string>(
      opts: ToOptions<RegisteredRouter, TFrom, TTo>
    ): string =>
      // `opts` is type-checked against the route tree at the call site; cast to
      // buildLocation's own param type (the two ToOptions variants differ only
      // in their internal params-reducer shape).
      router.buildLocation(opts as Parameters<typeof router.buildLocation>[0]).href,
    [router]
  );
}
